import type { AppNode, FunnelStage } from '../types';

const DEFAULT_STAGE_INSET = 1.2;

export function getStageBounds(
  stage: FunnelStage,
  stageOrder: readonly FunnelStage[],
  dividers: readonly number[],
): { start: number; end: number } {
  const index = stageOrder.indexOf(stage);
  const fallbackIndex = index >= 0 ? index : 0;
  const fallbackSize = 100 / Math.max(stageOrder.length, 1);
  const start = dividers[fallbackIndex] ?? fallbackIndex * fallbackSize;
  const end = dividers[fallbackIndex + 1] ?? start + fallbackSize;
  return { start, end };
}

export function clampYToStage(
  y: number,
  stage: FunnelStage,
  stageOrder: readonly FunnelStage[],
  dividers: readonly number[],
): number {
  const { start, end } = getStageBounds(stage, stageOrder, dividers);
  const inset = Math.min(DEFAULT_STAGE_INSET, Math.max((end - start) / 4, 0));
  return Math.max(start + inset, Math.min(end - inset, y));
}

export function getStageAtY(
  y: number,
  stageOrder: readonly FunnelStage[],
  dividers: readonly number[],
): FunnelStage {
  const safeY = Math.max(0, Math.min(dividers[stageOrder.length] ?? 100, y));
  const index = stageOrder.findIndex((_, stageIndex) => {
    const start = dividers[stageIndex] ?? (stageIndex * 100) / stageOrder.length;
    const end = dividers[stageIndex + 1] ?? ((stageIndex + 1) * 100) / stageOrder.length;
    return safeY >= start && (safeY < end || stageIndex === stageOrder.length - 1);
  });
  return stageOrder[index >= 0 ? index : stageOrder.length - 1];
}

export function moveNodeToStage(
  node: AppNode,
  targetStage: FunnelStage,
  stageOrder: readonly FunnelStage[],
  dividers: readonly number[],
): AppNode {
  const { start, end } = getStageBounds(targetStage, stageOrder, dividers);
  return {
    ...node,
    stage: targetStage,
    y: Number((start + (end - start) / 2).toFixed(1)),
  };
}

